import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
  Card, Row, Col, Select, Button, Space, Typography, Divider, Alert, Form, 
  Input, InputNumber, Switch, Modal, Upload, message, Progress, Tag, Tooltip, Checkbox
} from 'antd';
import { 
  UploadOutlined, PlusOutlined, DeleteOutlined, SaveOutlined, 
  UndoOutlined, RedoOutlined, ThunderboltOutlined, WarningOutlined,
  RotateLeftOutlined, ClearOutlined, ImportOutlined
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../constants';
import { useGetLayoutByIdQuery, useGetVehicleProfilesQuery, useUpdateLayoutMutation } from '../../store/api/calculationsApi';
import { useGetApplicationsQuery } from '../../store/api/applicationsApi';

const { Title, Text } = Typography;
const { Option } = Select;

// Интерфейсы
interface CargoItem {
  id: string;
  name: string;
  lengthMm: number;
  widthMm: number;
  heightMm: number;
  weightKg: number;
  quantity: number;
  stackable: boolean;
  rotAllowed: boolean;
  color: string;
}

interface PlacedItem extends Omit<CargoItem, 'quantity'> {
  x: number;
  y: number;
  z: number;
  rotation: number;
}

interface VehicleSpec {
  id: string;
  name: string;
  internalLengthMm: number;
  internalWidthMm: number;
  internalHeightMm: number;
  maxPayloadKg: number;
  // Для трала: максимальные габариты со свесом
  maxLengthWithOverhangMm?: number;
  maxWidthWithOverhangMm?: number;
  maxHeightWithOverhangMm?: number;
  isCustom?: boolean;
}

// Предустановленные типы транспорта
const defaultVehicleTypes: VehicleSpec[] = [
  { id: 'tent_13_6', name: 'Тент 13.6м', internalLengthMm: 13600, internalWidthMm: 2450, internalHeightMm: 2700, maxPayloadKg: 20000 },
  { id: 'tent_16', name: 'Тент 16м', internalLengthMm: 16000, internalWidthMm: 2450, internalHeightMm: 2700, maxPayloadKg: 20000 },
  { id: 'ref_13_6', name: 'Рефрижератор 13.6м', internalLengthMm: 13400, internalWidthMm: 2400, internalHeightMm: 2500, maxPayloadKg: 20000 },
  { id: 'flatbed_13_6', name: 'Борт 13.6м', internalLengthMm: 13600, internalWidthMm: 2450, internalHeightMm: 3000, maxPayloadKg: 20000 },
  { id: 'lowbed', name: 'Низкорамный трал', internalLengthMm: 12000, internalWidthMm: 2550, internalHeightMm: 3500, maxPayloadKg: 40000, maxLengthWithOverhangMm: 20000, maxWidthWithOverhangMm: 3500, maxHeightWithOverhangMm: 4500 },
];

const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

const UnifiedCalculationPage: React.FC = () => {
  const params = useParams();
  const layoutId = Number(params.id);
  const { data: layout, isLoading: layoutLoading } = useGetLayoutByIdQuery(layoutId, { skip: !layoutId });
  const { data: profiles } = useGetVehicleProfilesQuery();
  const [updateLayout] = useUpdateLayoutMutation();
  const { data: applicationsList } = useGetApplicationsQuery({ page: 1, limit: 100 });
  
  const [form] = Form.useForm();
  const [vehicleForm] = Form.useForm();
  const vehicleContainerRef = useRef<HTMLDivElement>(null);
  
  // Состояние
  const [cargoItems, setCargoItems] = useState<CargoItem[]>([]);
  const [placedItems, setPlacedItems] = useState<PlacedItem[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleSpec | null>(null);
  const [customVehicleTypes, setCustomVehicleTypes] = useState<VehicleSpec[]>([]);
  const [gapMm, setGapMm] = useState(50);
  const [wallGapMm, setWallGapMm] = useState(0);
  const [screenScale, setScreenScale] = useState(0.06);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Настройки максимальных габаритов (для негабарита/свеса)
  const [maxLengthMm, setMaxLengthMm] = useState<number | null>(null);
  const [maxWidthMm, setMaxWidthMm] = useState<number | null>(null);
  const [maxHeightMm, setMaxHeightMm] = useState<number | null>(null);
  
  // Выбранные грузы для размещения
  const [selectedCargoIds, setSelectedCargoIds] = useState<Set<string>>(new Set());
  
  // Модалки
  const [importFromAppOpen, setImportFromAppOpen] = useState(false);
  const [importExcelOpen, setImportExcelOpen] = useState(false);
  const [addVehicleModalOpen, setAddVehicleModalOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | undefined>();
  
  // История для Undo/Redo
  const [history, setHistory] = useState<{ placed: PlacedItem[], cargo: CargoItem[] }[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  const fileRef = useRef<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Парсер для InputNumber — поддержка запятой как разделителя
  const parseDecimal = (value: string | undefined): number => {
    if (!value) return 0;
    // Заменяем запятую на точку и парсим
    const normalized = value.toString().replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Все типы транспорта (предустановленные + пользовательские)
  const vehicleTypes = useMemo(() => [...defaultVehicleTypes, ...customVehicleTypes], [customVehicleTypes]);

  // Загрузка данных из layout
  useEffect(() => {
    if (layout && profiles) {
      // Загружаем профиль ТС
      const profile = profiles.find((p: any) => p.id === layout.vehicleProfileId);
      if (profile) {
        setSelectedVehicle({
          id: `profile_${profile.id}`,
          name: profile.name,
          internalLengthMm: Math.round((profile.lengthM || 13.6) * 1000),
          internalWidthMm: Math.round((profile.widthM || 2.5) * 1000),
          internalHeightMm: Math.round((profile.heightM || 2.7) * 1000),
          maxPayloadKg: Math.round((profile.payloadT || 20) * 1000)
        });
      }
      
      // Загружаем грузы (поддержка разных форматов полей)
      const inputCargo = (layout.inputPayload as any)?.cargo || [];
      const loadedCargo: CargoItem[] = inputCargo.map((item: any, index: number) => {
        // Поддержка разных форматов: lengthM, length_m, lengthMm
        const lengthM = Number(item.lengthM || item.length_m || item.lengthMm / 1000 || 0);
        const widthM = Number(item.widthM || item.width_m || item.widthMm / 1000 || 0);
        const heightM = Number(item.heightM || item.height_m || item.heightMm / 1000 || 0);
        const weightKg = Number(item.weightPerItemKg || item.weight_kg || item.weightKg || 0);
        
        return {
          id: `cargo_${item.id || index}_${Date.now()}`,
          name: item.name || 'Груз',
          lengthMm: Math.round(lengthM * 1000),
          widthMm: Math.round(widthM * 1000),
          heightMm: Math.round(heightM * 1000),
          weightKg,
          quantity: Number(item.quantity || 1),
          stackable: Boolean(item.stackable),
          rotAllowed: item.rotAllowed !== false,
          color: colors[index % colors.length]
        };
      });
      setCargoItems(loadedCargo);
      
      // Загружаем размещённые грузы
      const resultPlacements = (layout.resultPayload as any)?.layout2D?.placements || [];
      if (resultPlacements.length > 0) {
        const loadedPlacements: PlacedItem[] = resultPlacements.map((p: any, index: number) => ({
          id: `placed_${index}`,
          name: p.name || 'Груз',
          lengthMm: Math.round((p.l || p.length || 0) * 1000),
          widthMm: Math.round((p.w || p.width || 0) * 1000),
          heightMm: Math.round((p.h || p.height || 0) * 1000),
          weightKg: Number(p.weightPerItemKg || 0),
          stackable: false,
          rotAllowed: true,
          color: colors[index % colors.length],
          x: Math.round((p.x || 0) * 1000),
          y: Math.round((p.y || 0) * 1000),
          z: Math.round((p.z || 0) * 1000),
          rotation: p.rotation || 0
        }));
        setPlacedItems(loadedPlacements);
      }
    }
  }, [layout, profiles]);

  // Сохранение в историю
  const saveToHistory = useCallback(() => {
    const newState = { placed: [...placedItems], cargo: [...cargoItems] };
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newState);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [placedItems, cargoItems, history, historyIndex]);

  // Undo/Redo
  const undo = () => {
    if (historyIndex > 0) {
      const prev = history[historyIndex - 1];
      setPlacedItems(prev.placed);
      setCargoItems(prev.cargo);
      setHistoryIndex(historyIndex - 1);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const next = history[historyIndex + 1];
      setPlacedItems(next.placed);
      setCargoItems(next.cargo);
      setHistoryIndex(historyIndex + 1);
    }
  };

  // Проверка коллизий
  const checkCollision = useCallback((item: { lengthMm: number; widthMm: number; heightMm: number }, 
    x: number, y: number, z: number, rotation: number = 0, excludeId?: string): boolean => {
    if (!selectedVehicle) return true;

    let length = item.lengthMm;
    let width = item.widthMm;
    if (rotation === 90 || rotation === 270) {
      [length, width] = [width, length];
    }

    // Проверка границ (с небольшой погрешностью для округления - 1мм)
    const tolerance = 1;
    if (x < wallGapMm - tolerance || y < wallGapMm - tolerance ||
        x + length > selectedVehicle.internalLengthMm - wallGapMm + tolerance ||
        y + width > selectedVehicle.internalWidthMm - wallGapMm + tolerance) {
      return true;
    }

    // Проверка коллизий с другими грузами
    return placedItems.some(placed => {
      if (excludeId && placed.id === excludeId) return false;
      
      let pLength = placed.lengthMm;
      let pWidth = placed.widthMm;
      if (placed.rotation === 90 || placed.rotation === 270) {
        [pLength, pWidth] = [pWidth, pLength];
      }

      return !(x + length + gapMm <= placed.x ||
               placed.x + pLength + gapMm <= x ||
               y + width + gapMm <= placed.y ||
               placed.y + pWidth + gapMm <= y);
    });
  }, [selectedVehicle, placedItems, gapMm, wallGapMm]);

  // Автоматическое размещение всех грузов
  const autoPlaceAll = useCallback(() => {
    if (!selectedVehicle) {
      message.warning('Выберите тип транспорта');
      return;
    }

    if (cargoItems.length === 0) {
      message.warning('Добавьте грузы для размещения');
      return;
    }

    saveToHistory();
    const newPlacedItems: PlacedItem[] = [...placedItems]; // Сохраняем уже размещённые
    const step = 50; // Шаг поиска в мм

    // Определяем какие грузы размещать (выбранные или все)
    const itemsToPlace = selectedCargoIds.size > 0 
      ? cargoItems.filter(item => selectedCargoIds.has(item.id))
      : cargoItems;

    if (itemsToPlace.length === 0) {
      message.warning('Нет грузов для размещения');
      return;
    }

    // Максимальные габариты (с учётом настроек свеса)
    const maxL = maxLengthMm || selectedVehicle.internalLengthMm;
    const maxW = maxWidthMm || selectedVehicle.internalWidthMm;

    // Разворачиваем грузы по количеству
    const expandedItems: CargoItem[] = [];
    itemsToPlace.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        expandedItems.push({ ...item, id: `${item.id}_${i}`, quantity: 1 });
      }
    });

    // Сортируем: сначала по ширине (чтобы заполнять ряды), потом по площади
    expandedItems.sort((a, b) => {
      // Определяем оптимальную ширину (меньшая сторона для размещения по ширине кузова)
      const aOptWidth = a.rotAllowed ? Math.min(a.lengthMm, a.widthMm) : a.widthMm;
      const bOptWidth = b.rotAllowed ? Math.min(b.lengthMm, b.widthMm) : b.widthMm;
      // Сначала по площади (большие первыми), потом по ширине
      const areaDiff = (b.lengthMm * b.widthMm) - (a.lengthMm * a.widthMm);
      if (Math.abs(areaDiff) > 100000) return areaDiff;
      return bOptWidth - aOptWidth;
    });

    let placedCount = 0;
    const notPlacedItems: CargoItem[] = [];

    for (const item of expandedItems) {
      let placed = false;
      let bestPosition: { x: number; y: number; rotation: number; score: number } | null = null;
      
      // Определяем порядок поворотов: предпочитаем тот, где груз занимает меньше длины кузова
      const rotations: number[] = [];
      if (item.rotAllowed) {
        // Если длина > ширины, поворачиваем на 90° чтобы длинная сторона шла по ширине кузова
        if (item.lengthMm > item.widthMm) {
          rotations.push(90, 0);
        } else {
          rotations.push(0, 90);
        }
      } else {
        rotations.push(0);
      }

      // Ищем лучшую позицию (минимизируем занимаемую длину кузова)
      for (const rotation of rotations) {
        if (placed) break;

        const length = rotation === 90 ? item.widthMm : item.lengthMm;
        const width = rotation === 90 ? item.lengthMm : item.widthMm;

        // Проверяем, помещается ли груз вообще
        if (length > maxL - 2 * wallGapMm || width > maxW - 2 * wallGapMm) continue;

        // Сначала заполняем по ширине (Y), потом по длине (X) - так занимаем меньше длины кузова
        for (let x = wallGapMm; x <= maxL - length - wallGapMm && !placed; x += step) {
          for (let y = wallGapMm; y <= maxW - width - wallGapMm; y += step) {
            // Проверяем коллизию с уже размещёнными
            const hasCollision = newPlacedItems.some(p => {
              const pLength = p.rotation === 90 || p.rotation === 270 ? p.widthMm : p.lengthMm;
              const pWidth = p.rotation === 90 || p.rotation === 270 ? p.lengthMm : p.widthMm;
              return !(x + length + gapMm <= p.x || p.x + pLength + gapMm <= x ||
                       y + width + gapMm <= p.y || p.y + pWidth + gapMm <= y);
            });

            if (!hasCollision) {
              // Оценка позиции: чем меньше X, тем лучше (ближе к началу кузова)
              const score = x + length;
              if (!bestPosition || score < bestPosition.score) {
                bestPosition = { x, y, rotation, score };
              }
              // Берём первую найденную позицию в текущем ряду X
              placed = true;
              break;
            }
          }
        }
      }

      if (bestPosition) {
        newPlacedItems.push({
          ...item,
          x: bestPosition.x,
          y: bestPosition.y,
          z: 0,
          rotation: bestPosition.rotation,
          id: `placed_${Date.now()}_${newPlacedItems.length}`
        });
        placedCount++;
      } else {
        // Груз не поместился - всё равно добавляем в кузов (будет с ошибкой)
        // Ставим в угол или на последнюю доступную позицию
        const rotation = item.rotAllowed && item.lengthMm > item.widthMm ? 90 : 0;
        const length = rotation === 90 ? item.widthMm : item.lengthMm;
        
        // Ищем позицию в конце кузова
        let posX = wallGapMm;
        let posY = wallGapMm;
        
        // Находим самую дальнюю занятую позицию по X
        newPlacedItems.forEach(p => {
          const pRotated = p.rotation === 90 || p.rotation === 270;
          const pLength = pRotated ? p.widthMm : p.lengthMm;
          const endX = p.x + pLength + gapMm;
          if (endX > posX) posX = endX;
        });
        
        // Если не помещается даже в конце, ставим с выходом за границу
        if (posX + length > maxL) {
          posX = maxL - length / 2; // Частично за границей
        }
        
        newPlacedItems.push({
          ...item,
          x: posX,
          y: posY,
          z: 0,
          rotation,
          id: `placed_${Date.now()}_${newPlacedItems.length}`
        });
        notPlacedItems.push(item); // Для подсчёта
      }
    }

    setPlacedItems(newPlacedItems);
    
    // Удаляем все грузы из списка (они теперь в кузове)
    if (selectedCargoIds.size > 0) {
      setCargoItems(cargoItems.filter(item => !selectedCargoIds.has(item.id)));
      setSelectedCargoIds(new Set());
    } else {
      setCargoItems([]);
    }
    
    if (notPlacedItems.length > 0) {
      message.warning(`Размещено ${placedCount} грузов. Не поместилось: ${notPlacedItems.length}`);
    } else {
      message.success(`Размещено ${placedCount} грузов`);
    }
  }, [selectedVehicle, cargoItems, placedItems, gapMm, wallGapMm, maxLengthMm, maxWidthMm, selectedCargoIds, saveToHistory]);

  // Очистить размещение (вернуть грузы в список)
  const clearPlacement = () => {
    saveToHistory();
    // Возвращаем грузы в список
    const returnedCargo: CargoItem[] = [];
    const cargoMap = new Map<string, CargoItem>();

    placedItems.forEach(item => {
      const baseId = item.id.split('_').slice(0, 2).join('_');
      if (cargoMap.has(baseId)) {
        const existing = cargoMap.get(baseId)!;
        existing.quantity += 1;
      } else {
        cargoMap.set(baseId, { ...item, quantity: 1, id: baseId });
      }
    });

    cargoMap.forEach(item => returnedCargo.push(item));
    setCargoItems([...cargoItems, ...returnedCargo]);
    setPlacedItems([]);
    message.info('Размещение очищено, грузы возвращены в список');
  };

  // Очистить размещение БЕЗ возврата грузов в список
  const clearPlacementWithoutReturn = () => {
    if (placedItems.length === 0) return;
    saveToHistory();
    setPlacedItems([]);
    message.info('Кузов очищен');
  };

  // Добавление пользовательского типа кузова
  const addCustomVehicle = async () => {
    try {
      const values = await vehicleForm.validateFields();
      const newVehicle: VehicleSpec = {
        id: `custom_${Date.now()}`,
        name: values.name || `Кузов ${customVehicleTypes.length + 1}`,
        internalLengthMm: Math.round((values.lengthM || 13.6) * 1000),
        internalWidthMm: Math.round((values.widthM || 2.45) * 1000),
        internalHeightMm: Math.round((values.heightM || 2.7) * 1000),
        maxPayloadKg: Math.round((values.payloadT || 20) * 1000),
        maxLengthWithOverhangMm: values.maxLengthM ? Math.round(values.maxLengthM * 1000) : undefined,
        maxWidthWithOverhangMm: values.maxWidthM ? Math.round(values.maxWidthM * 1000) : undefined,
        maxHeightWithOverhangMm: values.maxHeightM ? Math.round(values.maxHeightM * 1000) : undefined,
        isCustom: true
      };
      
      setCustomVehicleTypes([...customVehicleTypes, newVehicle]);
      setSelectedVehicle(newVehicle);
      setAddVehicleModalOpen(false);
      vehicleForm.resetFields();
      message.success(`Добавлен тип кузова: ${newVehicle.name}`);
    } catch (e) {
      // Валидация не прошла
    }
  };

  // Удаление пользовательского типа кузова
  const removeCustomVehicle = (id: string) => {
    setCustomVehicleTypes(customVehicleTypes.filter(v => v.id !== id));
    if (selectedVehicle?.id === id) {
      setSelectedVehicle(null);
    }
    message.info('Тип кузова удалён');
  };

  // Счётчик для авто-нумерации грузов
  const [cargoCounter, setCargoCounter] = useState(1);

  // Добавление груза
  const addCargoItem = async () => {
    try {
      // Валидируем обязательные поля
      await form.validateFields(['lengthM', 'widthM', 'heightM', 'weightKg']);
      
      // Получаем ВСЕ значения формы (включая quantity, name, stackable, rotAllowed)
      const values = form.getFieldsValue();
      const autoName = values.name || `Место ${cargoCounter}`;
      
      // Получаем quantity из формы, преобразуем в число
      const quantity = Number(values.quantity) || 1;
      const baseColor = colors[(cargoItems.length) % colors.length];
      
      // Создаём отдельные элементы для каждого места
      const newItems: CargoItem[] = [];
      for (let i = 0; i < quantity; i++) {
        const itemName = quantity > 1 ? `${autoName} (${i + 1}/${quantity})` : autoName;
        newItems.push({
          id: `cargo_${Date.now()}_${i}`,
          name: itemName,
          lengthMm: Math.round((values.lengthM || 0) * 1000),
          widthMm: Math.round((values.widthM || 0) * 1000),
          heightMm: Math.round((values.heightM || 0) * 1000),
          weightKg: values.weightKg || 0,
          quantity: 1,
          stackable: values.stackable || false,
          rotAllowed: values.rotAllowed !== false,
          color: baseColor
        });
      }
      
      setCargoItems([...cargoItems, ...newItems]);
      setCargoCounter(cargoCounter + quantity);
      // НЕ очищаем поля - оставляем данные для повторного добавления
      message.success(`Добавлено ${quantity} мест`);
    } catch (e) {
      // Валидация не прошла
    }
  };

  // Удаление груза из списка
  const removeCargoItem = (id: string) => {
    setCargoItems(cargoItems.filter(item => item.id !== id));
  };

  // Переключение штабелирования для груза в списке
  const toggleCargoStackable = (id: string) => {
    setCargoItems(cargoItems.map(item => 
      item.id === id ? { ...item, stackable: !item.stackable } : item
    ));
  };

  // Переключение поворота для груза в списке
  const toggleCargoRotAllowed = (id: string) => {
    setCargoItems(cargoItems.map(item => 
      item.id === id ? { ...item, rotAllowed: !item.rotAllowed } : item
    ));
  };

  // Выбор/снятие выбора груза
  const toggleCargoSelection = (id: string) => {
    const newSelection = new Set(selectedCargoIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedCargoIds(newSelection);
  };

  // Выбрать все грузы
  const selectAllCargo = () => {
    setSelectedCargoIds(new Set(cargoItems.map(item => item.id)));
  };

  // Снять выбор со всех
  const deselectAllCargo = () => {
    setSelectedCargoIds(new Set());
  };

  // Очистка списка грузов
  const clearCargoList = () => {
    if (cargoItems.length === 0) return;
    saveToHistory();
    setCargoItems([]);
    message.info('Список грузов очищен');
  };

  // Перетаскивание груза из списка в кузов
  const [draggingFromList, setDraggingFromList] = useState<CargoItem | null>(null);

  const handleCargoListDragStart = (e: React.DragEvent, item: CargoItem) => {
    setDraggingFromList(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleVehicleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggingFromList || !selectedVehicle || !vehicleContainerRef.current) return;

    const rect = vehicleContainerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / screenScale;
    const y = (e.clientY - rect.top) / screenScale;

    // Центрируем груз на точке drop
    const dropX = Math.max(0, x - draggingFromList.lengthMm / 2);
    const dropY = Math.max(0, y - draggingFromList.widthMm / 2);

    // Проверяем коллизию
    if (!checkCollision(draggingFromList, dropX, dropY, 0, 0)) {
      saveToHistory();
      
      // Добавляем в размещённые (без quantity, т.к. PlacedItem не содержит это поле)
      const newPlaced: PlacedItem = {
        id: `placed_${Date.now()}`,
        name: draggingFromList.name,
        lengthMm: draggingFromList.lengthMm,
        widthMm: draggingFromList.widthMm,
        heightMm: draggingFromList.heightMm,
        weightKg: draggingFromList.weightKg,
        stackable: draggingFromList.stackable,
        rotAllowed: draggingFromList.rotAllowed,
        color: draggingFromList.color,
        x: dropX,
        y: dropY,
        z: 0,
        rotation: 0
      };
      setPlacedItems([...placedItems, newPlaced]);

      // Уменьшаем количество в списке или удаляем
      if (draggingFromList.quantity > 1) {
        setCargoItems(cargoItems.map(c => 
          c.id === draggingFromList.id 
            ? { ...c, quantity: c.quantity - 1 }
            : c
        ));
      } else {
        setCargoItems(cargoItems.filter(c => c.id !== draggingFromList.id));
      }

      message.success(`Груз "${draggingFromList.name}" размещён`);
    } else {
      message.warning('Невозможно разместить здесь (коллизия или за границей)');
    }

    setDraggingFromList(null);
  };

  const handleVehicleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Удаление размещённого груза
  const removePlacedItem = (id: string) => {
    saveToHistory();
    const item = placedItems.find(p => p.id === id);
    if (item) {
      // Возвращаем в список грузов
      const baseId = item.id.split('_').slice(0, 2).join('_');
      const existingIndex = cargoItems.findIndex(c => c.name === item.name);
      if (existingIndex >= 0) {
        const updated = [...cargoItems];
        updated[existingIndex].quantity += 1;
        setCargoItems(updated);
      } else {
        setCargoItems([...cargoItems, { ...item, quantity: 1, id: baseId }]);
      }
    }
    setPlacedItems(placedItems.filter(p => p.id !== id));
  };

  // Поворот груза
  const rotatePlacedItem = (id: string) => {
    saveToHistory();
    setPlacedItems(placedItems.map(item => {
      if (item.id === id) {
        const newRotation = (item.rotation + 90) % 360;
        return { ...item, rotation: newRotation };
      }
      return item;
    }));
  };

  // Импорт из заявки
  const handleImportFromApplication = () => {
    if (!selectedApplicationId) return;
    const app = applicationsList?.applications?.find((a: any) => a.id === selectedApplicationId);
    if (!app) return;

    console.log('📦 Импорт грузов из заявки:', app);
    console.log('📦 Грузы заявки:', app.cargo);

    const imported: CargoItem[] = (app.cargo || []).map((c: any, index: number) => {
      console.log(`📦 Груз ${index}:`, c);
      
      // Поддержка разных форматов полей (из БД приходят lengthM, widthM, heightM)
      const lengthM = Number(c.lengthM) || Number(c.length_m) || Number(c.lengthMm) / 1000 || 0;
      const widthM = Number(c.widthM) || Number(c.width_m) || Number(c.widthMm) / 1000 || 0;
      const heightM = Number(c.heightM) || Number(c.height_m) || Number(c.heightMm) / 1000 || 0;
      const weightKg = Number(c.weightPerItem) || Number(c.weight_kg) || Number(c.weightKg) || Number(c.totalWeightKg) / (c.quantity || 1) || 0;
      
      console.log(`📦 Габариты груза ${c.name}:`, { lengthM, widthM, heightM, weightKg });
      
      return {
        id: `cargo_app_${c.id}_${Date.now()}`,
        name: c.name || 'Груз',
        lengthMm: Math.round(lengthM * 1000),
        widthMm: Math.round(widthM * 1000),
        heightMm: Math.round(heightM * 1000),
        weightKg,
        quantity: c.quantity || 1,
        stackable: false,
        rotAllowed: true,
        color: colors[(cargoItems.length + index) % colors.length]
      };
    });

    console.log('📦 Импортированные грузы:', imported);

    setCargoItems([...cargoItems, ...imported]);
    setImportFromAppOpen(false);
    setSelectedApplicationId(undefined);
    message.success(`Импортировано ${imported.length} грузов`);
  };

  // Импорт Excel
  const doUpload = async () => {
    if (!fileRef.current) {
      message.warning('Выберите файл');
      return;
    }
    const formData = new FormData();
    formData.append('file', fileRef.current);
    setUploading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/calculations/layouts/${layoutId}/import`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData,
        credentials: 'include'
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || 'Ошибка импорта');
      message.success(`Импортировано: ${json.data?.imported || 0}`);
      setImportExcelOpen(false);
      window.location.reload();
    } catch (e: any) {
      message.error(e?.message || 'Ошибка импорта');
    } finally {
      setUploading(false);
    }
  };

  // Сохранение в БД
  const saveToDatabase = async () => {
    if (!layoutId) return;

    try {
      // Сохраняем грузы
      const cargoForSave = cargoItems.map(item => ({
        id: item.id,
        name: item.name,
        lengthM: item.lengthMm / 1000,
        widthM: item.widthMm / 1000,
        heightM: item.heightMm / 1000,
        weightPerItemKg: item.weightKg,
        quantity: item.quantity,
        stackable: item.stackable,
        rotAllowed: item.rotAllowed
      }));

      // Сохраняем размещение
      const placements = placedItems.map(item => ({
        x: item.x / 1000,
        y: item.y / 1000,
        z: item.z / 1000,
        l: item.lengthMm / 1000,
        w: item.widthMm / 1000,
        h: item.heightMm / 1000,
        name: item.name,
        rotation: item.rotation,
        weightPerItemKg: item.weightKg
      }));

      const resultPayload = {
        layout2D: { strategy: 'visual_editor', placements },
        summary: {
          totalItems: cargoItems.reduce((s, c) => s + c.quantity, 0) + placedItems.length,
          placedItems: placedItems.length,
          unplacedItems: cargoItems.reduce((s, c) => s + c.quantity, 0),
          totalWeightKg: placedItems.reduce((s, p) => s + p.weightKg, 0)
        }
      };

      await updateLayout({
        id: layoutId,
        body: {
          inputPayload: { cargo: cargoForSave },
          resultPayload
        }
      }).unwrap();

      message.success('Сохранено');
    } catch (e) {
      message.error('Ошибка сохранения');
      console.error(e);
    }
  };

  // Конвертация координат мыши в мм
  const getMouseInMm = useCallback((e: React.MouseEvent | MouseEvent) => {
    const container = vehicleContainerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const pxX = e.clientX - rect.left;
    const pxY = e.clientY - rect.top;
    return { xMm: pxX / screenScale, yMm: pxY / screenScale };
  }, [screenScale]);

  // Обработчики перетаскивания
  const handleMouseDown = (e: React.MouseEvent, itemId: string) => {
    const item = placedItems.find(p => p.id === itemId);
    if (!item) return;
    const mouseMm = getMouseInMm(e);
    if (!mouseMm) return;

    const itemCenterX = item.x + (item.lengthMm / 2);
    const itemCenterY = item.y + (item.widthMm / 2);

    setDraggedItem(itemId);
    setDragOffset({
      x: mouseMm.xMm - itemCenterX,
      y: mouseMm.yMm - itemCenterY
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!draggedItem || !selectedVehicle) return;
    const mouseMm = getMouseInMm(e);
    if (!mouseMm) return;

    const item = placedItems.find(i => i.id === draggedItem);
    if (!item) return;

    const rotated = item.rotation === 90 || item.rotation === 270;
    const length = rotated ? item.widthMm : item.lengthMm;
    const width = rotated ? item.lengthMm : item.widthMm;

    const newCenterX = mouseMm.xMm - dragOffset.x;
    const newCenterY = mouseMm.yMm - dragOffset.y;
    const newX = Math.max(0, Math.min(newCenterX - length / 2, selectedVehicle.internalLengthMm - length));
    const newY = Math.max(0, Math.min(newCenterY - width / 2, selectedVehicle.internalWidthMm - width));

    setPlacedItems(prev => prev.map(p =>
      p.id === draggedItem ? { ...p, x: newX, y: newY } : p
    ));
  }, [draggedItem, selectedVehicle, placedItems, dragOffset, getMouseInMm]);

  const handleMouseUp = useCallback(() => {
    if (draggedItem) {
      saveToHistory();
    }
    setDraggedItem(null);
    setDragOffset({ x: 0, y: 0 });
  }, [draggedItem, saveToHistory]);

  // Глобальные обработчики мыши
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Zoom колёсиком
  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.005 : 0.005;
      setScreenScale(prev => Math.max(0.02, Math.min(0.15, prev + delta)));
    }
  };

  // Статистика
  const stats = useMemo(() => {
    if (!selectedVehicle) return null;

    const totalWeight = placedItems.reduce((s, p) => s + p.weightKg, 0);
    const totalVolume = placedItems.reduce((s, p) => s + p.lengthMm * p.widthMm * p.heightMm, 0);
    const vehicleVolume = selectedVehicle.internalLengthMm * selectedVehicle.internalWidthMm * selectedVehicle.internalHeightMm;

    // Фактически занятая длина кузова по раскладке
    let occupiedLengthMm = 0;
    if (placedItems.length > 0) {
      let minX = Number.POSITIVE_INFINITY;
      let maxX = 0;
      placedItems.forEach(item => {
        const rotated = item.rotation === 90 || item.rotation === 270;
        const length = rotated ? item.widthMm : item.lengthMm;
        const startX = item.x;
        const endX = item.x + length;
        if (startX < minX) minX = startX;
        if (endX > maxX) maxX = endX;
      });
      occupiedLengthMm = Math.max(0, maxX - minX);
    }

    const lengthPercent = selectedVehicle.internalLengthMm > 0
      ? (occupiedLengthMm / selectedVehicle.internalLengthMm) * 100
      : 0;

    // Максимальная высота (с учётом настроек)
    // Используем ?? вместо || чтобы правильно обработать null и 0
    const maxH = maxHeightMm ?? selectedVehicle.internalHeightMm;

    // Подсчёт ошибок
    let errorsCount = 0;
    placedItems.forEach(item => {
      const rotated = item.rotation === 90 || item.rotation === 270;
      const length = rotated ? item.widthMm : item.lengthMm;
      const width = rotated ? item.lengthMm : item.widthMm;

      // Проверка выхода за границы (с небольшой погрешностью для округления - 1мм)
      const tolerance = 1;
      if (item.x < wallGapMm - tolerance || item.y < wallGapMm - tolerance ||
          item.x + length > selectedVehicle.internalLengthMm - wallGapMm + tolerance ||
          item.y + width > selectedVehicle.internalWidthMm - wallGapMm + tolerance) {
        errorsCount++;
        return;
      }

      // Проверка высоты (с небольшой погрешностью для округления)
      if (maxH && item.heightMm > maxH + 1) { // +1мм погрешность
        errorsCount++;
        return;
      }

      const hasCollision = placedItems.some(other => {
        if (other.id === item.id) return false;
        const oRotated = other.rotation === 90 || other.rotation === 270;
        const oLength = oRotated ? other.widthMm : other.lengthMm;
        const oWidth = oRotated ? other.lengthMm : other.widthMm;
        return !(item.x + length + gapMm <= other.x ||
                 other.x + oLength + gapMm <= item.x ||
                 item.y + width + gapMm <= other.y ||
                 other.y + oWidth + gapMm <= item.y);
      });
      if (hasCollision) errorsCount++;
    });

    return {
      totalWeight,
      totalVolume,
      weightPercent: (totalWeight / selectedVehicle.maxPayloadKg) * 100,
      volumePercent: (totalVolume / vehicleVolume) * 100,
      occupiedLengthMm,
      lengthPercent,
      placedCount: placedItems.length,
      unplacedCount: cargoItems.reduce((s, c) => s + c.quantity, 0),
      errorsCount
    };
  }, [selectedVehicle, placedItems, cargoItems, gapMm, wallGapMm, maxHeightMm]);

  const isOverweight = stats && selectedVehicle && stats.totalWeight > selectedVehicle.maxPayloadKg;
  const isOverLength = stats && selectedVehicle && stats.occupiedLengthMm > selectedVehicle.internalLengthMm;

  if (layoutLoading) {
    return <div style={{ padding: 24, textAlign: 'center' }}>Загрузка...</div>;
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Расчёт размещения #{layoutId}
        </Title>
        <Space>
          <Button icon={<UndoOutlined />} onClick={undo} disabled={historyIndex <= 0}>Отменить</Button>
          <Button icon={<RedoOutlined />} onClick={redo} disabled={historyIndex >= history.length - 1}>Повторить</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={saveToDatabase}>Сохранить</Button>
        </Space>
      </div>

      <Row gutter={16}>
        {/* Левая панель: настройки транспорта и добавление груза */}
        <Col xs={24} lg={5}>
          {/* Настройки кузова */}
          <Card title="Транспорт" size="small" style={{ marginBottom: 12 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Тип:</Text>
                <Select
                  style={{ width: '100%', marginTop: 4 }}
                  placeholder="Выберите тип"
                  value={selectedVehicle?.id}
                  onChange={(value) => {
                    const v = vehicleTypes.find(t => t.id === value);
                    setSelectedVehicle(v || null);
                  }}
                >
                  {vehicleTypes.map(v => (
                    <Option key={v.id} value={v.id}>
                      {v.name} {v.isCustom && '✏️'}
                    </Option>
                  ))}
                </Select>
                <Button 
                  type="dashed" 
                  size="small" 
                  icon={<PlusOutlined />}
                  onClick={() => setAddVehicleModalOpen(true)}
                  style={{ width: '100%', marginTop: 4 }}
                >
                  Добавить тип кузова
                </Button>
              </div>

              {selectedVehicle && (
                <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                  <div>
                    <strong>Внутренние:</strong> {(selectedVehicle.internalLengthMm / 1000).toFixed(1)} × {(selectedVehicle.internalWidthMm / 1000).toFixed(1)} × {(selectedVehicle.internalHeightMm / 1000).toFixed(1)} м
                  </div>
                  <div>Грузоподъёмность: {(selectedVehicle.maxPayloadKg / 1000).toFixed(1)} т</div>
                  {selectedVehicle.maxLengthWithOverhangMm && (
                    <div style={{ color: '#faad14' }}>
                      <strong>Макс. со свесом:</strong> {(selectedVehicle.maxLengthWithOverhangMm / 1000).toFixed(1)} × {((selectedVehicle.maxWidthWithOverhangMm || selectedVehicle.internalWidthMm) / 1000).toFixed(1)} × {((selectedVehicle.maxHeightWithOverhangMm || selectedVehicle.internalHeightMm) / 1000).toFixed(1)} м
                    </div>
                  )}
                  {selectedVehicle.isCustom && (
                    <Button 
                      type="link" 
                      danger 
                      size="small" 
                      onClick={() => removeCustomVehicle(selectedVehicle.id)}
                      style={{ padding: 0, height: 'auto' }}
                    >
                      Удалить этот тип
                    </Button>
                  )}
                </div>
              )}

              <Divider style={{ margin: '8px 0' }} />

              <div>
                <Text>Зазор между грузами:</Text>
                <InputNumber
                  style={{ width: '100%' }}
                  value={gapMm}
                  onChange={(v) => setGapMm(v ?? 0)}
                  min={0}
                  max={500}
                  addonAfter="мм"
                />
              </div>

              <div>
                <Text>Зазор от стенок:</Text>
                <InputNumber
                  style={{ width: '100%' }}
                  value={wallGapMm}
                  onChange={(v) => setWallGapMm(v ?? 0)}
                  min={0}
                  max={500}
                  addonAfter="мм"
                />
              </div>

              <Divider style={{ margin: '8px 0' }} />
              
              <div>
                <Text strong style={{ fontSize: 11 }}>Макс. габариты (свес/негабарит):</Text>
                <Row gutter={4} style={{ marginTop: 4 }}>
                  <Col span={8}>
                    <InputNumber
                      size="small"
                      style={{ width: '100%' }}
                      placeholder="Длина"
                      value={maxLengthMm ? maxLengthMm / 1000 : undefined}
                      onChange={(v) => setMaxLengthMm(v ? Math.round(v * 1000) : null)}
                      min={1}
                      max={30}
                      step={0.1}
                      parser={parseDecimal}
                    />
                  </Col>
                  <Col span={8}>
                    <InputNumber
                      size="small"
                      style={{ width: '100%' }}
                      placeholder="Ширина"
                      value={maxWidthMm ? maxWidthMm / 1000 : undefined}
                      onChange={(v) => setMaxWidthMm(v ? Math.round(v * 1000) : null)}
                      min={1}
                      max={6}
                      step={0.1}
                      parser={parseDecimal}
                    />
                  </Col>
                  <Col span={8}>
                    <InputNumber
                      size="small"
                      style={{ width: '100%' }}
                      placeholder="Высота"
                      value={maxHeightMm ? maxHeightMm / 1000 : undefined}
                      onChange={(v) => setMaxHeightMm(v ? Math.round(v * 1000) : null)}
                      min={1}
                      max={6}
                      step={0.1}
                      parser={parseDecimal}
                    />
                  </Col>
                </Row>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Пусто = используются внутренние размеры
                </Text>
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <div>
                <Text>Масштаб:</Text>
                <Select
                  style={{ width: '100%' }}
                  value={screenScale}
                  onChange={setScreenScale}
                >
                  <Option value={0.03}>1:33</Option>
                  <Option value={0.04}>1:25</Option>
                  <Option value={0.05}>1:20</Option>
                  <Option value={0.06}>1:17</Option>
                  <Option value={0.08}>1:12</Option>
                  <Option value={0.1}>1:10</Option>
                </Select>
              </div>
            </Space>
          </Card>

          {/* Добавление груза */}
          <Card title="Добавить груз" size="small" style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical" size="small">
              <Form.Item name="name" label="Название">
                <Input placeholder={`Авто: Место ${cargoCounter}`} />
              </Form.Item>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item name="lengthM" label="Длина (м)" rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} step={0.1} parser={parseDecimal} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="widthM" label="Ширина (м)" rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} step={0.1} parser={parseDecimal} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="heightM" label="Высота (м)" rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} step={0.1} parser={parseDecimal} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="weightKg" label="Вес (кг)" rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} parser={parseDecimal} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="quantity" label="Кол-во" initialValue={1}>
                    <InputNumber style={{ width: '100%' }} min={1} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="stackable" valuePropName="checked">
                    <Switch checkedChildren="Штабелируется" unCheckedChildren="Не штаб." />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="rotAllowed" valuePropName="checked" initialValue={true}>
                    <Switch checkedChildren="Поворот 90°" unCheckedChildren="Без поворота" />
                  </Form.Item>
                </Col>
              </Row>
              <Button type="primary" icon={<PlusOutlined />} onClick={addCargoItem} block>
                Добавить
              </Button>
            </Form>

            <Divider style={{ margin: '12px 0' }} />

            <Space wrap>
              <Button size="small" onClick={() => setImportFromAppOpen(true)} icon={<ImportOutlined />}>
                Из заявки
              </Button>
              <Button size="small" onClick={() => setImportExcelOpen(true)} icon={<UploadOutlined />}>
                Из Excel
              </Button>
            </Space>
          </Card>
        </Col>

        {/* Центральная панель: визуальный редактор + список грузов */}
        <Col xs={24} lg={14}>
          <Card 
            title="Схема размещения" 
            size="small"
            extra={
              <Space>
                <Button size="small" icon={<ClearOutlined />} onClick={clearPlacement}>
                  Очистить
                </Button>
              </Space>
            }
          >
            {!selectedVehicle ? (
              <Alert message="Выберите тип транспорта" type="info" />
            ) : (
              <div 
                style={{ 
                  overflow: 'auto', 
                  background: '#f0f0f0', 
                  padding: 16,
                  borderRadius: 4
                }}
                onWheel={handleWheel}
              >
                {/* Кузов */}
                <div
                  ref={vehicleContainerRef}
                  onDrop={handleVehicleDrop}
                  onDragOver={handleVehicleDragOver}
                  style={{
                    width: selectedVehicle.internalLengthMm * screenScale,
                    height: selectedVehicle.internalWidthMm * screenScale,
                    background: draggingFromList ? '#e6f7ff' : '#fff',
                    border: draggingFromList ? '3px dashed #1890ff' : '2px solid #333',
                    position: 'relative',
                    backgroundImage: draggingFromList ? 'none' : `
                      linear-gradient(to right, #e0e0e0 1px, transparent 1px),
                      linear-gradient(to bottom, #e0e0e0 1px, transparent 1px)
                    `,
                    backgroundSize: `${1000 * screenScale}px ${1000 * screenScale}px`,
                    transition: 'border 0.2s, background 0.2s'
                  }}
                >
                  {/* Размещённые грузы */}
                  {placedItems.map(item => {
                    const rotated = item.rotation === 90 || item.rotation === 270;
                    const drawLength = rotated ? item.widthMm : item.lengthMm;
                    const drawWidth = rotated ? item.lengthMm : item.widthMm;

                    // Максимальная высота кузова (с учётом настроек или стандартная)
                    // Используем ?? вместо || чтобы правильно обработать null и 0
                    const maxH = maxHeightMm ?? selectedVehicle.internalHeightMm;

                    // Проверка ошибок (с небольшой погрешностью для округления - 1мм)
                    const tolerance = 1;
                    const isOutOfBounds = 
                      item.x < wallGapMm - tolerance ||
                      item.y < wallGapMm - tolerance ||
                      item.x + drawLength > selectedVehicle.internalLengthMm - wallGapMm + tolerance ||
                      item.y + drawWidth > selectedVehicle.internalWidthMm - wallGapMm + tolerance;

                    // Проверка превышения высоты (с небольшой погрешностью для округления)
                    const isTooTall = maxH && item.heightMm > maxH + 1; // +1мм погрешность

                    const hasCollision = placedItems.some(other => {
                      if (other.id === item.id) return false;
                      const oRotated = other.rotation === 90 || other.rotation === 270;
                      const oLength = oRotated ? other.widthMm : other.lengthMm;
                      const oWidth = oRotated ? other.lengthMm : other.widthMm;
                      return !(item.x + drawLength + gapMm <= other.x ||
                               other.x + oLength + gapMm <= item.x ||
                               item.y + drawWidth + gapMm <= other.y ||
                               other.y + oWidth + gapMm <= item.y);
                    });

                    const hasError = isOutOfBounds || hasCollision || isTooTall;

                    return (
                      <Tooltip
                        key={item.id}
                        title={
                          <div>
                            <div><strong>{item.name}</strong></div>
                            <div>{(item.lengthMm / 1000).toFixed(2)}×{(item.widthMm / 1000).toFixed(2)}×{(item.heightMm / 1000).toFixed(2)} м</div>
                            <div>{item.weightKg} кг</div>
                            {isOutOfBounds && <div style={{ color: '#ff4d4f' }}>⚠️ За границей!</div>}
                            {hasCollision && <div style={{ color: '#ff4d4f' }}>⚠️ Коллизия!</div>}
                            {isTooTall && <div style={{ color: '#ff4d4f' }}>⚠️ Превышает высоту кузова!</div>}
                          </div>
                        }
                      >
                        <div
                          style={{
                            position: 'absolute',
                            left: item.x * screenScale,
                            top: item.y * screenScale,
                            width: drawLength * screenScale,
                            height: drawWidth * screenScale,
                            backgroundColor: hasError ? '#ff4d4f' : item.color,
                            opacity: draggedItem === item.id ? 0.7 : 0.85,
                            border: hasError ? '2px dashed #a8071a' : '1px solid rgba(0,0,0,0.3)',
                            borderRadius: 2,
                            cursor: 'move',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontSize: Math.max(8, Math.min(12, drawLength * screenScale / 8)),
                            color: '#fff',
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                            overflow: 'hidden',
                            userSelect: 'none',
                            boxShadow: hasError ? '0 0 8px rgba(255,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                          onMouseDown={(e) => handleMouseDown(e, item.id)}
                        >
                          <div style={{ fontWeight: 'bold', textAlign: 'center' }}>{item.name}</div>
                          {hasError && <WarningOutlined style={{ fontSize: 16 }} />}
                          
                          {/* Кнопки управления */}
                          <div 
                            style={{ 
                              position: 'absolute', 
                              top: 2, 
                              right: 2, 
                              display: 'flex', 
                              gap: 2 
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={<RotateLeftOutlined />}
                              style={{ 
                                color: '#fff', 
                                background: 'rgba(0,0,0,0.3)',
                                minWidth: 20,
                                height: 20,
                                padding: 0
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                rotatePlacedItem(item.id);
                              }}
                            />
                            <Button
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              style={{ 
                                color: '#fff', 
                                background: 'rgba(255,0,0,0.5)',
                                minWidth: 20,
                                height: 20,
                                padding: 0
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                removePlacedItem(item.id);
                              }}
                            />
                          </div>
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>

                {/* Подписи размеров */}
                <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: '#666' }}>
                  {(selectedVehicle.internalLengthMm / 1000).toFixed(1)} м × {(selectedVehicle.internalWidthMm / 1000).toFixed(1)} м
                </div>
              </div>
            )}
          </Card>

          {/* Список грузов - под кузовом */}
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Грузы для размещения ({cargoItems.reduce((s, c) => s + c.quantity, 0)})</span>
                {selectedCargoIds.size > 0 && (
                  <Tag color="blue" style={{ margin: 0 }}>
                    Выбрано: {selectedCargoIds.size}
                  </Tag>
                )}
              </div>
            }
            size="small"
            style={{ marginTop: 12 }}
            extra={
              <Space size={4}>
                <Button size="small" onClick={selectAllCargo} disabled={cargoItems.length === 0}>
                  Все
                </Button>
                <Button size="small" onClick={deselectAllCargo} disabled={selectedCargoIds.size === 0}>
                  Снять
                </Button>
                <Button 
                  size="small" 
                  icon={<ClearOutlined />}
                  onClick={clearCargoList}
                  disabled={cargoItems.length === 0}
                />
                <Button 
                  type="primary" 
                  size="small" 
                  icon={<ThunderboltOutlined />}
                  onClick={autoPlaceAll}
                  disabled={cargoItems.length === 0}
                >
                  {selectedCargoIds.size > 0 ? `Авто (${selectedCargoIds.size})` : 'Авто'}
                </Button>
              </Space>
            }
          >
            {cargoItems.length === 0 ? (
              <Text type="secondary">Добавьте грузы через форму слева или импортируйте из заявки/Excel</Text>
            ) : (
              <div 
                style={{ 
                  maxHeight: 300,
                  overflowY: 'auto',
                  padding: 4
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {cargoItems.map(item => (
                    <div 
                      key={item.id}
                      draggable
                      onDragStart={(e) => handleCargoListDragStart(e, item)}
                      onDragEnd={() => setDraggingFromList(null)}
                      style={{
                        padding: '8px 12px',
                        background: selectedCargoIds.has(item.id) ? '#e6f7ff' : '#f5f5f5',
                        borderRadius: 6,
                        borderLeft: `5px solid ${item.color}`,
                        cursor: 'grab',
                        transition: 'all 0.2s',
                        border: selectedCargoIds.has(item.id) ? '2px solid #1890ff' : '1px solid #d9d9d9',
                        minWidth: 180,
                        maxWidth: 240,
                        flex: '0 0 auto'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedCargoIds.has(item.id)) {
                          e.currentTarget.style.background = '#fafafa';
                          e.currentTarget.style.borderColor = '#40a9ff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = selectedCargoIds.has(item.id) ? '#e6f7ff' : '#f5f5f5';
                        e.currentTarget.style.borderColor = selectedCargoIds.has(item.id) ? '#1890ff' : '#d9d9d9';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                        <Checkbox 
                          checked={selectedCargoIds.has(item.id)}
                          onChange={() => toggleCargoSelection(item.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ marginRight: 8 }}
                        />
                        <div style={{ flex: 1, fontSize: 12, overflow: 'hidden' }}>
                          <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.name}
                          </div>
                          <div style={{ color: '#666', fontSize: 11 }}>
                            {(item.lengthMm / 1000).toFixed(2)}×{(item.widthMm / 1000).toFixed(2)}×{(item.heightMm / 1000).toFixed(2)} м
                          </div>
                          <div style={{ color: '#888', fontSize: 11 }}>
                            {item.weightKg} кг
                          </div>
                        </div>
                        <Button 
                          type="text" 
                          size="small" 
                          danger 
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCargoItem(item.id);
                          }}
                          style={{ marginLeft: 4, minWidth: 24, padding: 0 }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                        <Tag 
                          color={item.stackable ? 'green' : 'default'}
                          style={{ cursor: 'pointer', margin: 0, fontSize: 11 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCargoStackable(item.id);
                          }}
                        >
                          {item.stackable ? '📦 Штаб.' : '— Не штаб.'}
                        </Tag>
                        <Tag 
                          color={item.rotAllowed ? 'blue' : 'default'}
                          style={{ cursor: 'pointer', margin: 0, fontSize: 11 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCargoRotAllowed(item.id);
                          }}
                        >
                          {item.rotAllowed ? '🔄 Поворот' : '🔒 Фикс.'}
                        </Tag>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {cargoItems.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#999' }}>
                💡 Перетащите груз в кузов или выберите и нажмите "Авто"
              </div>
            )}
          </Card>
        </Col>

        {/* Правая панель: статистика */}
        <Col xs={24} lg={5}>
          <Card title="Статистика" size="small" style={{ marginBottom: 12 }}>
            {stats ? (
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Размещено:</Text>
                  <div style={{ fontWeight: 'bold' }}>{stats.placedCount} грузов</div>
                </div>

                {stats.unplacedCount > 0 && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>Не размещено:</Text>
                    <div style={{ color: '#faad14' }}>{stats.unplacedCount} грузов</div>
                  </div>
                )}

                {stats.errorsCount > 0 && (
                  <Alert
                    message={`Ошибок: ${stats.errorsCount}`}
                    type="error"
                    showIcon
                    style={{ padding: '4px 8px' }}
                  />
                )}

                <Divider style={{ margin: '8px 0' }} />

                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Вес:</Text>
                  <Progress
                    percent={Math.min(100, Math.round(stats.weightPercent))}
                    size="small"
                    status={isOverweight ? 'exception' : 'normal'}
                  />
                  <div style={{ fontSize: 11, color: isOverweight ? '#ff4d4f' : '#666' }}>
                    {(stats.totalWeight / 1000).toFixed(2)} / {(selectedVehicle?.maxPayloadKg || 0) / 1000} т
                  </div>
                </div>

                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Объём:</Text>
                  <Progress
                    percent={Math.min(100, Math.round(stats.volumePercent))}
                    size="small"
                    strokeColor={stats.volumePercent > 90 ? '#faad14' : '#1890ff'}
                  />
                  <div style={{ fontSize: 11, color: '#666' }}>
                    {(stats.totalVolume / 1e9).toFixed(2)} м³
                  </div>
                </div>

                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Длина занята:</Text>
                  <Progress
                    percent={Math.min(100, Math.round(stats.lengthPercent))}
                    size="small"
                    status={isOverLength ? 'exception' : 'normal'}
                    strokeColor={stats.lengthPercent > 90 ? '#faad14' : '#1890ff'}
                  />
                  <div style={{ fontSize: 11, color: isOverLength ? '#ff4d4f' : '#666' }}>
                    {(stats.occupiedLengthMm / 1000).toFixed(2)} / {(selectedVehicle?.internalLengthMm || 0) / 1000} м
                  </div>
                </div>
              </Space>
            ) : (
              <Text type="secondary">Выберите транспорт</Text>
            )}
          </Card>

          {/* Список размещённых грузов */}
          <Card 
            title={`В кузове (${placedItems.length})`} 
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <Button 
                type="text" 
                size="small" 
                danger
                icon={<ClearOutlined />}
                onClick={clearPlacementWithoutReturn}
                disabled={placedItems.length === 0}
              />
            }
          >
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {placedItems.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 11 }}>Пусто</Text>
              ) : (
                placedItems.map((item, index) => (
                  <div 
                    key={item.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '3px 6px',
                      marginBottom: 2,
                      background: '#f0f9ff',
                      borderRadius: 3,
                      borderLeft: `3px solid ${item.color}`,
                      fontSize: 10
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold' }}>{index + 1}. {item.name}</div>
                      <div style={{ color: '#666' }}>
                        {(item.lengthMm / 1000).toFixed(2)}×{(item.widthMm / 1000).toFixed(2)} м
                        {item.rotation > 0 && <span style={{ color: '#1890ff' }}> 🔄{item.rotation}°</span>}
                      </div>
                    </div>
                    <Button 
                      type="text" 
                      size="small" 
                      danger 
                      icon={<DeleteOutlined />}
                      style={{ minWidth: 20, height: 20, padding: 0 }}
                      onClick={() => removePlacedItem(item.id)}
                    />
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card title="Легенда" size="small">
            <Space direction="vertical" size={4} style={{ fontSize: 10 }}>
              <div><Tag color="blue" style={{ fontSize: 10 }}>■</Tag> Обычный</div>
              <div><Tag color="red" style={{ fontSize: 10 }}>■</Tag> Ошибка</div>
              <div><Tag color="green" style={{ fontSize: 10 }}>■</Tag> Штабелируется</div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Модалка импорта из заявки */}
      <Modal
        title="Импорт грузов из заявки"
        open={importFromAppOpen}
        onCancel={() => setImportFromAppOpen(false)}
        onOk={handleImportFromApplication}
        okButtonProps={{ disabled: !selectedApplicationId }}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Выберите заявку"
          value={selectedApplicationId}
          onChange={(v) => setSelectedApplicationId(Number(v))}
          showSearch
          optionFilterProp="children"
        >
          {(applicationsList?.applications || []).map((a: any) => (
            <Option key={a.id} value={a.id}>{a.internalNumber} — {a.client?.name}</Option>
          ))}
        </Select>
      </Modal>

      {/* Модалка импорта Excel */}
      <Modal
        open={importExcelOpen}
        onCancel={() => setImportExcelOpen(false)}
        onOk={doUpload}
        okButtonProps={{ loading: uploading }}
        title="Импорт из Excel"
      >
        <p>Выберите файл Excel с колонками: name, quantity, length_m, width_m, height_m, weight_kg</p>
        <Upload beforeUpload={(file) => { fileRef.current = file; return false; }} maxCount={1} accept=".xlsx,.xls">
          <Button icon={<UploadOutlined />}>Выбрать файл</Button>
        </Upload>
      </Modal>

      {/* Модалка добавления типа кузова */}
      <Modal
        title="Добавить тип кузова"
        open={addVehicleModalOpen}
        onCancel={() => {
          setAddVehicleModalOpen(false);
          vehicleForm.resetFields();
        }}
        onOk={addCustomVehicle}
        okText="Добавить"
        width={500}
      >
        <Form form={vehicleForm} layout="vertical" size="small">
          <Form.Item name="name" label="Название">
            <Input placeholder="Например: Трал 20м" />
          </Form.Item>
          
          <Divider orientation="left" style={{ fontSize: 12 }}>Внутренние размеры кузова</Divider>
          
          <Row gutter={8}>
            <Col span={8}>
              <Form.Item name="lengthM" label="Длина (м)" rules={[{ required: true }]} initialValue={13.6}>
                <InputNumber style={{ width: '100%' }} min={1} max={30} step={0.1} parser={parseDecimal} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="widthM" label="Ширина (м)" rules={[{ required: true }]} initialValue={2.45}>
                <InputNumber style={{ width: '100%' }} min={1} max={5} step={0.1} parser={parseDecimal} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="heightM" label="Высота (м)" rules={[{ required: true }]} initialValue={2.7}>
                <InputNumber style={{ width: '100%' }} min={1} max={5} step={0.1} parser={parseDecimal} />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item name="payloadT" label="Грузоподъёмность (т)" rules={[{ required: true }]} initialValue={20}>
            <InputNumber style={{ width: '100%' }} min={1} max={100} step={1} parser={parseDecimal} />
          </Form.Item>

          <Divider orientation="left" style={{ fontSize: 12 }}>Максимальные габариты со свесом (для трала)</Divider>
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
            Оставьте пустыми, если свес не допускается
          </Text>
          
          <Row gutter={8}>
            <Col span={8}>
              <Form.Item name="maxLengthM" label="Макс. длина (м)">
                <InputNumber style={{ width: '100%' }} min={1} max={40} step={0.1} placeholder="—" parser={parseDecimal} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxWidthM" label="Макс. ширина (м)">
                <InputNumber style={{ width: '100%' }} min={1} max={6} step={0.1} placeholder="—" parser={parseDecimal} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxHeightM" label="Макс. высота (м)">
                <InputNumber style={{ width: '100%' }} min={1} max={6} step={0.1} placeholder="—" parser={parseDecimal} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default UnifiedCalculationPage;

